// The code run in the case statements in this file should be wrapped in blocks.
/* eslint-disable no-case-declarations */

import * as React from 'react';
import { REQUEST_CREATE_PANEL } from '@helpcenter/connect-fragment';
import {
	isRichTextAreaEnabled,
	isRadioButtonsSupportEnabled,
	isProformaTTVCUpdateMeasureEnabled,
	isUiModificationsSupportEnabled,
	isForgeCustomFieldsOnPortalViewEnabled,
	isTextFieldSupportEnabled,
	isMultiCheckboxSupportEnabled,
	isMultiSelectEnabled,
	isDateTimeSupportEnabled,
	isDueDateSupportEnabled,
	isJsmPortalRovoIntegrationEnabled,
} from '@helpcenter/feature-flags';
import type { AdditionalFieldData, FieldValueType } from '@helpcenter/field-types';
import type { ForgeRequestCreatePropertyFormData } from '@helpcenter/forge-ui-utils';
import { SkeletonLoadingWrapper } from '@helpcenter/loading';
import { ThemedButton } from '@helpcenter/themed-button';
import { isEqual as deepEquals, toNumber } from 'lodash';
import { FormattedMessage } from 'react-intl';
import SmoothCollapse from 'react-smooth-collapse';
import type { OrganisationResponse } from 'rest/organisations';
import type {
	FieldState,
	FieldValues,
	RequestCreateKBState,
	ReporterState,
} from 'state/persisted/request-create';
import { AutoFocus } from 'view/common/auto-focus';
import { AnonymousEmailField } from 'view/common/fields/anonymous-email-field';
import { AssetsPicker } from 'view/common/fields/assets-picker';
import { FormCascadingSelect as CascadingSelect } from 'view/common/fields/cascading-select';
import { CheckboxGroup, ControlledCheckboxGroup } from 'view/common/fields/checkbox-group';
import { ConnectFields } from 'view/common/fields/connect-fields';
import {
	DatePicker,
	ControlledDatePicker,
	ConditionalDatepicker,
} from 'view/common/fields/date-picker';
import { ControlledDateTimePicker, DateTimePicker } from 'view/common/fields/date-time-picker';
import { EntitlementPicker } from 'view/common/fields/entitlement-picker';
import { ForgeField } from 'view/common/fields/forge-field';
import { LabelPicker } from 'view/common/fields/label-picker';
import { MultiSelectField } from 'view/common/fields/multi-select-field';
import { MultiServicePicker } from 'view/common/fields/multi-service-picker';
import { MultiUserPicker } from 'view/common/fields/multi-user-picker';
import { OrganisationPicker } from 'view/common/fields/organisation-picker';
import { RadioGroup, ControlledRadioGroup } from 'view/common/fields/radio-group';
import { Reporter } from 'view/common/fields/reporter';
import { RequestCreateAttachmentField } from 'view/common/fields/request-create-attachment-field';
import { FormRichTextArea as RichTextArea } from 'view/common/fields/rich-text-area';
import { PciSecureTextPicker } from 'view/common/fields/pci-secure-text-picker';
import { SingleSelectField } from 'view/common/fields/single-select-field';
import { SummaryField } from 'view/common/fields/summary';
import { FormTextArea as TextArea } from 'view/common/fields/text-area';
import { ControlledTextField, TextField } from 'view/common/fields/text-field';
import { UnknownField } from 'view/common/fields/unknown-field';
import type { User } from 'view/common/fields/user-picker';
import { UserPicker } from 'view/common/fields/user-picker';
import { RecaptchaForm } from 'view/common/recaptcha-form';
import { RequestCreatePropertyPanelModule } from 'view/forge-ui/request-create-property-panel';
import i18n from 'view/request-create-form/messages';
import { ButtonGroup } from 'view/request-create-form/styled';
import withAnalyticsContext from '@atlaskit/analytics-next/withAnalyticsContext';
import withAnalyticsEvents, {
	type WithAnalyticsEventsProps,
} from '@atlaskit/analytics-next/withAnalyticsEvents';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import Spinner from '@atlaskit/spinner/spinner';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { CmdbObjectPicker } from '@atlassian/cmdb-help-center-components/src/ui/cmdb-object-picker';
import type { CmdbObjectPickerOption } from '@atlassian/cmdb-help-center-components/src/ui/cmdb-object-picker/cmdb-object-picker-dumb';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { createPlaceholderValueMap } from '@atlassian/help-center-common-util/cmdb-object-picker';
import memoizeOne from '@atlassian/help-center-common-util/memoize-one';
import {
	createAsyncProformaRequestCreate,
	type OwnProps as ProformaRequestCreateProps,
	type RegisterProformaForm,
} from '@atlassian/proforma-request-create';
import { FormErrorMessage } from '../form-error-message';
import type { FormErrors } from '../types';
import type { FieldType } from 'state/persisted/request-create/types';

export interface Props extends Partial<DefaultProps> {
	accountId?: string;
	projectId?: string;
	portalId: number;
	requestTypeId: number;
	knowledgeBase?: RequestCreateKBState;
	fields: FieldState[];
	fieldValues: FieldValues;
	urlParamFieldValueOverrides: FieldValues;
	formRef?: React.RefObject<HTMLFormElement>;
	proformaField?: React.ComponentType<ProformaRequestCreateProps> | null;
	onFieldChange: (
		name: string,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		value: any,
		notFromUserInteraction?: boolean,
		additionalFieldData?: object,
	) => void;
	onFieldBlurredByUser?: ({
		field,
		value,
	}: {
		field: string;
		value: unknown;
		additionalFieldData?: { rawObject: object; rawValue: unknown };
	}) => void;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
	onToggleFormExpanded: () => void;
	onCancel: () => void;
	enableSubmitButton: () => void;
	disableSubmitButton: () => void;
	onHoverSubmitButton?: () => void;
	className?: string;
	submitting?: boolean;
	submitDisabled: boolean;
	captchaEnabled: boolean;
	isCSM?: boolean;
	sitekey?: string;
	errors: FormErrors | undefined;
	submissionErrorCount: number;
	canCreateUsers: boolean;
	dateFormat?: string;
	dateTimeFormat?: string;
	firstDay?: number;
	useISO8601WeekNumbers?: boolean;
	isFormExpanded?: boolean;
	blockFormSubmission: (blocking: boolean) => void;
	isAttachmentUploading: boolean;
	registerProformaForm?: RegisterProformaForm;
	onProformaFormRendered?: () => void;
	locale: string;
	updateForgeRequestPropertyData: (data: ForgeRequestCreatePropertyFormData) => void;
}

interface DefaultProps {
	errors: FormErrors;
}

type PropsWithDefaultProps = Props & DefaultProps;

interface State {
	accountId: string | undefined;
	isNewReporter: boolean | undefined;
	asyncFormCreateAppLoaded: boolean;
	shouldHoldUFO: boolean;
	// Whether the Proforma form has finished rendering.
	isProformaFormRendered: boolean;
}

/**
 * Feature gate: block_request_create_submit_for_form_loading
 *
 * Disables the Send/Cancel buttons on portal request create while the Proforma form
 * has not finished rendering, so requests can't be submitted without form answers.
 */
const BLOCK_REQUEST_CREATE_SUBMIT_FOR_FORM_LOADING = 'block_request_create_submit_for_form_loading';

const isBlockRequestCreateSubmitForFormLoadingEnabled = (): boolean =>
	FeatureGates.checkGate(BLOCK_REQUEST_CREATE_SUBMIT_FOR_FORM_LOADING);

export class RequestFormDumb extends React.Component<Props & WithAnalyticsEventsProps, State> {
	static defaultProps: DefaultProps = {
		errors: {
			fieldErrors: {},
		},
	};

	state = {
		accountId: undefined,
		isNewReporter: undefined,
		asyncFormCreateAppLoaded: false,
		shouldHoldUFO: true,
		isProformaFormRendered: false,
	};

	unknownField: string | null = null;

	AsyncProformaRequestCreateInternal = createAsyncProformaRequestCreate({
		LoadingSkeleton: SkeletonLoadingWrapper,
		onLoaded: () => {
			this.setState({ asyncFormCreateAppLoaded: true });
		},
	});

	onReporterFieldChange = (
		name: string,
		value: string,
		_: boolean = false,
		additionalFieldData: AdditionalFieldData<User>,
	) => {
		const accountId =
			additionalFieldData &&
			additionalFieldData.rawObject &&
			additionalFieldData.rawObject.accountId;
		const isNewReporter = name === 'reporter' && additionalFieldData?.rawObject?.isNew;
		this.setState({ accountId, isNewReporter });
		this.props.onFieldChange(name, value);
	};

	onRecaptchaTokenChange = (token: string | null) => {
		this.props.onFieldChange('g-recaptcha-response', token);
	};

	shouldComponentUpdate(nextProps: Props, nextState: State) {
		// shouldComponentUpdate override is implemented for performance improvement, see FSD-2741
		const { fieldValues: nextFieldValues, ...nextRemainingProps } = nextProps;
		const { fieldValues, ...remainingProps } = this.props;

		if (!deepEquals(this.state, nextState) || !deepEquals(remainingProps, nextRemainingProps)) {
			return true;
		}

		// The organisation picker is dependent on the reporter field, so we should rerender in this case
		return nextFieldValues.reporter !== fieldValues.reporter;
	}

	componentDidMount() {
		const { createAnalyticsEvent } = this.props;

		if (createAnalyticsEvent) {
			const analyticsEvent = createAnalyticsEvent({
				analyticsType: OPERATIONAL_EVENT_TYPE,
				action: this.unknownField ? 'failed' : 'loaded',
				actionSubjectId: 'fields',
				attributes: {
					unknownField: this.unknownField,
				},
			});
			analyticsEvent.fire();
		}
	}

	renderFields(fields: FieldState[], submitting: boolean) {
		return fields
			.filter((field) => field.displayed)
			.map((field) => this.renderField(field, submitting));
	}

	renderReporterField(
		reporterState: ReporterState,
		baseProps: Omit<FieldState, 'type' | 'value' | 'valueOverride'>,
		submitting: boolean,
		fieldErrors: Record<string, string | undefined>,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		urlParamValueOverride: any,
		canCreateUsers: boolean,
	) {
		const defaultReporter = reporterState.defaultReporter
			? {
					email: reporterState.defaultReporter.emailAddress,
					iconUrl: reporterState.defaultReporter.avatar,
					value: reporterState.defaultReporter.id,
					label: reporterState.defaultReporter.displayName,
					accountId: reporterState.defaultReporter.accountId,
				}
			: undefined;
		const defaultReporterOverride: User | undefined = isJsmPortalRovoIntegrationEnabled()
			? (urlParamValueOverride as User)
			: undefined;

		return (
			<Reporter
				{...baseProps}
				portalId={this.props.portalId}
				label={reporterState.label || ''}
				onChange={this.onReporterFieldChange}
				key={reporterState.id}
				errorMessage={fieldErrors[reporterState.id]}
				disabled={submitting || !!reporterState.disabled}
				defaultReporter={defaultReporterOverride ?? defaultReporter}
				canCreateUser={canCreateUsers}
				createLabelType="create"
				fieldConfigId={reporterState.fieldConfigId}
			/>
		);
	}

	renderField(fieldState: FieldState, submitting: boolean) {
		const { type, value: _, valueOverride: __, ...baseProps } = fieldState;

		const {
			projectId,
			portalId,
			requestTypeId,
			onFieldChange,
			knowledgeBase,
			enableSubmitButton,
			disableSubmitButton,
			canCreateUsers,
			locale,
			dateFormat,
			dateTimeFormat,
			firstDay,
			useISO8601WeekNumbers,
			fieldValues,
			urlParamFieldValueOverrides,
			fields,
		} = this.props as PropsWithDefaultProps;

		const fieldErrors = (this.props.errors && this.props.errors.fieldErrors) || {};
		const errorReasonKey = this.props.errors && this.props.errors.errorReasonKey;
		// Suppressing existing violation. Please fix this.
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		const urlParamValueOverride = urlParamFieldValueOverrides[fieldState.id];
		const { reporter } = this.props.fieldValues;
		const shouldAttachmentRender = fields.some(
			(field) => field.type === 'attachment' && field.displayed,
		);

		switch (type) {
			case 'text':
				const textState = fieldState;
				if (textState.id === 'summary') {
					return (
						<SummaryField
							{...baseProps}
							requestTypeId={requestTypeId}
							knowledgeBase={knowledgeBase}
							// We need to rehydrate the default value if its defined.
							// If we don't do this the internal state is thrown away when re-rendered
							// and the field can never get back the current value.
							// This is currently handling the case when the summary field re-renders
							// from request tour onboarding dismissal.
							defaultValue={calculateDefaultSummaryValue(
								urlParamValueOverride,
								fieldValues[textState.id],
								textState.value,
							)}
							valueOverride={
								isUiModificationsSupportEnabled() ? textState.valueOverride : undefined
							}
							onChange={onFieldChange}
							onFieldBlurredByUser={this.props.onFieldBlurredByUser}
							key={textState.id}
							errorMessage={fieldErrors[textState.id]}
							disabled={submitting || !!textState.disabled}
						/>
					);
				}
				if (textState.id === 'email') {
					return (
						<AnonymousEmailField
							{...baseProps}
							key={textState.id}
							// Suppressing existing violation. Please fix this.
							defaultValue={
								// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
								urlParamValueOverride === undefined ? textState.value : urlParamValueOverride
							}
							onChange={onFieldChange}
							errorMessage={fieldErrors.email}
							disabled={submitting || !!textState.disabled}
							errorReasonKey={errorReasonKey}
						/>
					);
				}
				if (isTextFieldSupportEnabled()) {
					return (
						<ControlledTextField
							{...baseProps}
							// Suppressing existing violation. Please fix this.
							defaultValue={
								// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
								urlParamValueOverride === undefined ? textState.value : urlParamValueOverride
							}
							valueOverride={
								isUiModificationsSupportEnabled() ? textState.valueOverride : undefined
							}
							onChange={onFieldChange}
							onFieldBlurredByUser={this.props.onFieldBlurredByUser}
							key={textState.id}
							errorMessage={fieldErrors[textState.id]}
							disabled={submitting || !!textState.disabled}
							testId={'request-create-form-text-field'}
						/>
					);
				}
				return (
					<TextField
						{...baseProps}
						// Suppressing existing violation. Please fix this.
						defaultValue={
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							urlParamValueOverride === undefined ? textState.value : urlParamValueOverride
						}
						onChange={onFieldChange}
						key={textState.id}
						errorMessage={fieldErrors[textState.id]}
						disabled={submitting || !!textState.disabled}
						testId={'request-create-form-text-field'}
					/>
				);

			case 'checkbox': {
				const checkboxState = fieldState;
				const checkboxItems = checkboxState.items.map((item) => ({
					label: item.label,
					value: item.value,
				}));
				const checkboxProps = {
					...baseProps,
					items: checkboxItems,
					// Suppressing existing violation. Please fix this.
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					defaultValue: urlParamValueOverride || checkboxState.value,
					onChange: onFieldChange,
					key: checkboxState.id,
					errorMessage: fieldErrors[checkboxState.id],
					disabled: submitting || !!checkboxState.disabled,
				};

				return isMultiCheckboxSupportEnabled() ? (
					<ControlledCheckboxGroup
						{...checkboxProps}
						valueOverride={
							isUiModificationsSupportEnabled() ? checkboxState.valueOverride : undefined
						}
					/>
				) : (
					<CheckboxGroup {...checkboxProps} />
				);
			}

			case 'textarea':
				const textAreaState = fieldState;
				const hasMediaContext =
					FeatureGates.checkGate('request_create_inline_attachment') && shouldAttachmentRender;
				if (isRichTextAreaEnabled() && textAreaState.rendererType === 'atlassian-wiki-renderer') {
					const mediaContextProps =
						hasMediaContext && portalId
							? {
									mediaContext: { portalId, requestId: undefined },
								}
							: {};
					return (
						<RichTextArea
							{...baseProps}
							onChange={onFieldChange}
							defaultValue={calculateDefaultRichTextValue(
								urlParamValueOverride,
								fieldValues[textAreaState.id],
								textAreaState.value,
							)}
							key={textAreaState.id}
							errorMessage={fieldErrors[textAreaState.id]}
							disabled={submitting || !!textAreaState.disabled}
							fieldConfigs={{
								portalId: this.props.portalId,
								fieldConfigId: textAreaState.fieldConfigId,
								fieldName: textAreaState.id,
							}}
							valueOverride={
								isUiModificationsSupportEnabled() ? textAreaState.valueOverride : undefined
							}
							onFieldBlurredByUser={
								isUiModificationsSupportEnabled() ? this.props.onFieldBlurredByUser : undefined
							}
							{...mediaContextProps}
						/>
					);
				}
				return (
					<TextArea
						{...baseProps}
						minimumRows={6}
						onChange={onFieldChange}
						// Suppressing existing violation. Please fix this.
						defaultValue={
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							urlParamValueOverride === undefined ? textAreaState.value : urlParamValueOverride
						}
						key={textAreaState.id}
						errorMessage={fieldErrors[textAreaState.id]}
						disabled={submitting || !!textAreaState.disabled}
						valueOverride={
							isUiModificationsSupportEnabled() ? textAreaState.valueOverride : undefined
						}
						onFieldBlurredByUser={
							isUiModificationsSupportEnabled() ? this.props.onFieldBlurredByUser : undefined
						}
					/>
				);

			case 'radio':
				const radioGroupState = fieldState;
				const radioItems = radioGroupState.items.map((item) => {
					return {
						label: item.label,
						name: item.name,
						value: item.value,
					};
				});
				const radioProps = {
					...baseProps,
					items: radioItems,
					// Suppressing existing violation. Please fix this.
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					defaultValue: urlParamValueOverride || radioGroupState.value,
					onChange: onFieldChange,
					key: radioGroupState.id,
					errorMessage: fieldErrors[radioGroupState.id],
					disabled: submitting || !!radioGroupState.disabled,
				};

				return isRadioButtonsSupportEnabled() ? (
					<ControlledRadioGroup
						{...radioProps}
						valueOverride={
							isUiModificationsSupportEnabled() ? radioGroupState.valueOverride : undefined
						}
					/>
				) : (
					<RadioGroup {...radioProps} />
				);

			case 'date':
				const dateState = fieldState;
				return (
					<ConditionalDatepicker
						{...baseProps}
						// Suppressing existing violation. Please fix this.
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						defaultValue={urlParamValueOverride || dateState.defaultDate}
						valueOverride={isUiModificationsSupportEnabled() ? dateState.valueOverride : undefined}
						onChange={onFieldChange}
						key={dateState.id}
						errorMessage={fieldErrors[dateState.id]}
						disabled={submitting || !!dateState.disabled}
						dateFormat={dateFormat}
						firstDay={firstDay}
						locale={locale}
						useISO8601WeekNumbers={useISO8601WeekNumbers}
					/>
				);

			case 'duedate':
				const dueDateState = fieldState;
				return isDueDateSupportEnabled() ? (
					<ControlledDatePicker
						{...baseProps}
						// Suppressing existing violation. Please fix this.
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						defaultValue={urlParamValueOverride || dueDateState.defaultDate}
						valueOverride={
							isUiModificationsSupportEnabled() ? dueDateState.valueOverride : undefined
						}
						onChange={onFieldChange}
						key={dueDateState.id}
						errorMessage={fieldErrors[dueDateState.id]}
						disabled={submitting || !!dueDateState.disabled}
						dateFormat={dateFormat}
						firstDay={firstDay}
						locale={locale}
						useISO8601WeekNumbers={useISO8601WeekNumbers}
					/>
				) : (
					<DatePicker
						{...baseProps}
						// Suppressing existing violation. Please fix this.
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						defaultValue={urlParamValueOverride || dueDateState.defaultDate}
						onChange={onFieldChange}
						key={dueDateState.id}
						errorMessage={fieldErrors[dueDateState.id]}
						disabled={submitting || !!dueDateState.disabled}
						dateFormat={dateFormat}
						firstDay={firstDay}
						locale={locale}
						useISO8601WeekNumbers={useISO8601WeekNumbers}
					/>
				);

			case 'datetime':
				const dateTimeState = fieldState;
				return isDateTimeSupportEnabled() ? (
					<ControlledDateTimePicker
						{...baseProps}
						// Suppressing existing violation. Please fix this.
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						defaultValue={urlParamValueOverride || dateTimeState.defaultDate}
						valueOverride={
							isUiModificationsSupportEnabled() ? dateTimeState.valueOverride : undefined
						}
						onChange={onFieldChange}
						key={dateTimeState.id}
						errorMessage={fieldErrors[dateTimeState.id]}
						disabled={submitting || !!dateTimeState.disabled}
						dateTimeFormat={dateTimeFormat}
						firstDay={firstDay}
						useISO8601WeekNumbers={useISO8601WeekNumbers}
						locale={locale}
					/>
				) : (
					<DateTimePicker
						{...baseProps}
						// Suppressing existing violation. Please fix this.
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						defaultValue={urlParamValueOverride || dateTimeState.defaultDate}
						onChange={onFieldChange}
						key={dateTimeState.id}
						errorMessage={fieldErrors[dateTimeState.id]}
						disabled={submitting || !!dateTimeState.disabled}
						dateTimeFormat={dateTimeFormat}
						firstDay={firstDay}
						useISO8601WeekNumbers={useISO8601WeekNumbers}
						locale={locale}
					/>
				);

			case 'attachment':
				const attachmentState = fieldState;
				return (
					<RequestCreateAttachmentField
						{...baseProps}
						onChange={onFieldChange}
						key={attachmentState.id}
						disabled={submitting || !!attachmentState.disabled}
						errorMessage={fieldErrors[attachmentState.id]}
						portalId={this.props.portalId}
						blockFormSubmission={this.props.blockFormSubmission}
					/>
				);

			case 'reporter':
				return this.renderReporterField(
					fieldState,
					baseProps,
					submitting,
					fieldErrors,
					urlParamValueOverride,
					canCreateUsers,
				);

			case 'userpicker':
				const userPickerState = fieldState;
				const defaultUser = userPickerState.defaultUser
					? {
							email: userPickerState.defaultUser.emailAddress,
							iconUrl: userPickerState.defaultUser.avatar,
							value: userPickerState.defaultUser.id,
							label: userPickerState.defaultUser.displayName,
							accountId: userPickerState.defaultUser.accountId,
						}
					: undefined;

				const defaultUserOverride: User | undefined = isJsmPortalRovoIntegrationEnabled()
					? (urlParamValueOverride as User)
					: undefined;
				return (
					<UserPicker
						{...baseProps}
						portalId={this.props.portalId}
						label={userPickerState.label || ''}
						onChange={onFieldChange}
						key={userPickerState.id}
						errorMessage={fieldErrors[userPickerState.id]}
						disabled={submitting || !!userPickerState.disabled}
						defaultUser={defaultUserOverride ?? defaultUser}
						canCreateUser={canCreateUsers}
						createLabelType="add"
						fieldConfigId={userPickerState.fieldConfigId}
						valueOverride={
							isUiModificationsSupportEnabled() ? userPickerState.valueOverride : undefined
						}
					/>
				);

			case 'multiuserpicker':
				const multiUserPickerState = fieldState;
				const defaultUsers = (multiUserPickerState.defaultUsers || []).map((user) => ({
					email: user.emailAddress,
					iconUrl: user.avatar,
					value: user.id,
					label: user.displayName,
				}));

				const defaultMultiUsersOverride: User[] | undefined = isJsmPortalRovoIntegrationEnabled()
					? (urlParamValueOverride as User[])
					: undefined;

				const mergedDefaultUsers = defaultMultiUsersOverride ?? defaultUsers;

				return (
					<MultiUserPicker
						{...baseProps}
						portalId={this.props.portalId}
						label={multiUserPickerState.label || ''}
						onChange={onFieldChange}
						key={multiUserPickerState.id}
						errorMessage={fieldErrors[multiUserPickerState.id]}
						disabled={submitting || !!multiUserPickerState.disabled}
						defaultUsers={mergedDefaultUsers}
						fieldConfigId={multiUserPickerState.fieldConfigId}
						canAddUserByEmailAddress={canCreateUsers}
					/>
				);

			case 'organduserpicker':
				// Note: We are returning an empty fragment here to avoid rendering the unknown field.
				return <></>;

			case 'labelpicker':
				const labelPickerState = fieldState;
				return (
					<LabelPicker
						{...baseProps}
						customFieldId={labelPickerState.customFieldId}
						portalId={portalId}
						requestTypeId={requestTypeId}
						onChange={onFieldChange}
						key={labelPickerState.id}
						errorMessage={fieldErrors[labelPickerState.id]}
						disabled={submitting || !!labelPickerState.disabled}
						// Suppressing existing violation. Please fix this.
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						defaultLabels={urlParamValueOverride || labelPickerState.defaultLabels}
						valueOverride={
							isUiModificationsSupportEnabled() ? labelPickerState.valueOverride : undefined
						}
					/>
				);

			case 'organisationpicker':
				const orgState = fieldState;
				const defaultOrganisationOverride: OrganisationResponse | undefined =
					isJsmPortalRovoIntegrationEnabled()
						? (urlParamValueOverride as OrganisationResponse)
						: undefined;

				return (
					// @ts-ignore
					<OrganisationPicker
						{...baseProps}
						projectId={projectId}
						portalId={portalId}
						requestTypeId={requestTypeId}
						onChange={onFieldChange}
						key={orgState.id}
						// Suppressing existing violation. Please fix this.
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						organisationUserEmail={reporter}
						isNewReporter={this.state.isNewReporter}
						disabled={submitting || !!orgState.disabled}
						enableSubmitButton={enableSubmitButton}
						disableSubmitButton={disableSubmitButton}
						shareWithOrgByDefault={orgState.shareWithOrgByDefault}
						defaultOrganisation={defaultOrganisationOverride}
					/>
				);

			case 'assetspicker':
				const assetPickerState = fieldState;
				return (
					<AssetsPicker
						{...baseProps}
						label={assetPickerState.label || ''}
						onChange={onFieldChange}
						key={assetPickerState.id}
						disabled={submitting || !!assetPickerState.disabled}
						fieldConfigId={assetPickerState.fieldConfigId}
						accountId={this.state.accountId}
						portalId={portalId}
						errorMessage={fieldErrors[assetPickerState.id]}
					/>
				);

			case 'multiservicepicker':
				const multiServicePickerState = fieldState;
				let defaultMultiServicesOverride: string[] | undefined;
				if (isJsmPortalRovoIntegrationEnabled() && urlParamValueOverride) {
					// Handle both string and array formats
					if (Array.isArray(urlParamValueOverride)) {
						defaultMultiServicesOverride = urlParamValueOverride as string[];
					} else if (typeof urlParamValueOverride === 'string') {
						defaultMultiServicesOverride = [urlParamValueOverride];
					}
				}

				return (
					<MultiServicePicker
						{...baseProps}
						label={multiServicePickerState.label || ''}
						onChange={onFieldChange}
						key={multiServicePickerState.id}
						disabled={submitting || !!multiServicePickerState.disabled}
						errorMessage={fieldErrors[multiServicePickerState.id]}
						defaultServices={defaultMultiServicesOverride}
						value={fieldValues[multiServicePickerState.id] as string[]}
					/>
				);

			case 'cmdbobjectpicker':
				const cmdbObjectPickerState = fieldState;
				const fieldValueMapReducer = (
					accumulator: string[][],
					currentField: FieldState,
				): string[][] => {
					// Suppressing existing violation. Please fix this.
					return accumulator.concat([
						// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
						[currentField.id, fieldValues[currentField.id]?.toString() || ''],
					]);
				};

				// Disable field if form is submitting
				// OR if the field does not have a valid configuration (and therefore, cannot provide any values to pick)
				const isCmdbObjectPickerDisabled =
					submitting || !cmdbObjectPickerState.hasValidConfig || !!cmdbObjectPickerState.disabled;

				return (
					<CmdbObjectPicker
						{...baseProps}
						{...cmdbObjectPickerState}
						onChange={onFieldChange}
						key={cmdbObjectPickerState.id}
						disabled={isCmdbObjectPickerDisabled}
						errorMessage={fieldErrors[cmdbObjectPickerState.id]}
						portalId={portalId}
						requestTypeId={requestTypeId}
						fieldValueMap={fields.reduce(fieldValueMapReducer, [])}
						placeholderValues={createPlaceholderValueMap(
							fieldValues,
							cmdbObjectPickerState.placeholderKeys,
						)}
						initialValues={urlParamValueOverride as CmdbObjectPickerOption[]}
					/>
				);

			case 'select':
				const selectState = fieldState;
				return (
					<SingleSelectField
						{...baseProps}
						label={selectState.label || ''}
						onChange={onFieldChange}
						key={selectState.id}
						errorMessage={fieldErrors[selectState.id]}
						disabled={submitting || !!selectState.disabled}
						// Suppressing existing violation. Please fix this.
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						defaultValue={urlParamValueOverride || selectState.defaultValue}
						valueOverride={
							isUiModificationsSupportEnabled() ? selectState.valueOverride : undefined
						}
						options={selectState.options}
					/>
				);

			case 'multiselect':
				const multiSelectState = fieldState;
				return (
					<MultiSelectField
						{...baseProps}
						label={multiSelectState.label || ''}
						onChange={onFieldChange}
						key={multiSelectState.id}
						errorMessage={fieldErrors[multiSelectState.id]}
						disabled={submitting || !!multiSelectState.disabled}
						// Suppressing existing violation. Please fix this.
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						defaultValue={urlParamValueOverride || multiSelectState.defaultValue}
						valueOverride={isMultiSelectEnabled() ? multiSelectState.valueOverride : undefined}
						options={multiSelectState.options}
					/>
				);

			case 'cascadingselect':
				const cascadingSelectState = fieldState;
				return (
					<CascadingSelect
						{...baseProps}
						actionSubjectId="cascadingSelectField"
						label={cascadingSelectState.label || ''}
						onChange={onFieldChange}
						key={cascadingSelectState.id}
						errorMessage={fieldErrors[cascadingSelectState.id]}
						disabled={submitting || !!cascadingSelectState.disabled}
						// Suppressing existing violation. Please fix this.
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						defaultValue={urlParamValueOverride || cascadingSelectState.value}
						options={cascadingSelectState.options}
						valueOverride={
							isUiModificationsSupportEnabled() ? cascadingSelectState.valueOverride : undefined
						}
					/>
				);

			case 'cardnumber':
				const sdCardNumberState = fieldState;
				return (
					<PciSecureTextPicker
						{...baseProps}
						label={sdCardNumberState.label || ''}
						defaultValue={
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							urlParamValueOverride || sdCardNumberState.value
						}
						onChange={onFieldChange}
						key={sdCardNumberState.id}
						errorMessage={fieldErrors[sdCardNumberState.id]}
						disabled={submitting || !!sdCardNumberState.disabled}
					/>
				);

			case 'entitlementpicker':
				const entitlementState = fieldState;
				const defaultEntitlementOverride: string | undefined = isJsmPortalRovoIntegrationEnabled()
					? (urlParamValueOverride as string)
					: undefined;

				return (
					<EntitlementPicker
						{...baseProps}
						onChange={onFieldChange}
						label={entitlementState.label || ''}
						value={fieldValues[entitlementState.id] as string}
						key={entitlementState.id}
						reporterId={reporter as string}
						isNewReporter={this.state.isNewReporter}
						disabled={submitting || !!entitlementState.disabled}
						enableSubmitButton={enableSubmitButton}
						disableSubmitButton={disableSubmitButton}
						errorMessage={fieldErrors[entitlementState.id]}
						defaultEntitlement={defaultEntitlementOverride}
					/>
				);

			case 'forge':
				const forgeState = fieldState;
				return (
					<ForgeField
						{...baseProps}
						jiraFieldType={
							isForgeCustomFieldsOnPortalViewEnabled()
								? undefined
								: (forgeState.jiraFieldType as string)
						}
						extension={isForgeCustomFieldsOnPortalViewEnabled() ? forgeState.extension : undefined}
						key={forgeState.id}
						portalId={this.props.portalId}
						requestTypeId={this.props.requestTypeId}
						projectId={this.props.projectId}
						errorMessage={fieldErrors[forgeState.id]}
						value={fieldValues[forgeState.id] as FieldValueType}
						fallbackRender={(newType: FieldType) => {
							return this.renderField({ ...fieldState, type: newType } as FieldState, submitting);
						}}
						disabled={submitting || !!fieldState.disabled}
						enableSubmitButton={enableSubmitButton}
						disableSubmitButton={disableSubmitButton}
						onChange={onFieldChange}
					/>
				);

			default:
				const unknownFieldProps = fieldState;
				this.unknownField = type;
				return this.renderUnknownField(unknownFieldProps);
		}
	}

	renderUnknownField(fieldState: FieldState) {
		return <UnknownField id={fieldState.id} key={fieldState.id} />;
	}

	renderErrorMessage() {
		const { submissionErrorCount, errors } = this.props;

		if (errors && errors.submissionError) {
			return (
				<AutoFocus key={submissionErrorCount}>{<FormErrorMessage errors={errors} />}</AutoFocus>
			);
		}

		return null;
	}

	renderFormFinished() {
		this.props.onProformaFormRendered?.();

		if (this.state.shouldHoldUFO) {
			this.setState({
				shouldHoldUFO: false,
			});
		}

		if (!this.state.isProformaFormRendered) {
			this.setState({
				isProformaFormRendered: true,
			});
		}
	}

	render() {
		const {
			accountId,
			fields,
			fieldValues,
			errors,
			className,
			submitting,
			submitDisabled,
			onSubmit,
			onCancel,
			sitekey,
			requestTypeId,
			portalId,
			isFormExpanded,
			isAttachmentUploading,
			registerProformaForm,
			locale,
			knowledgeBase,
			updateForgeRequestPropertyData,
			formRef,
		} = this.props;

		const hasProformaForm = !!registerProformaForm;

		// Disable submission while the Proforma form has not finished rendering.
		const isProformaFormPending =
			hasProformaForm &&
			!this.state.isProformaFormRendered &&
			isBlockRequestCreateSubmitForFormLoadingEnabled();

		const AsyncProformaRequestCreateInternal = this.AsyncProformaRequestCreateInternal;

		const FormCreateApp = this.props.proformaField;
		let renderJiraFields = Boolean(FormCreateApp);

		if (!renderJiraFields) {
			// If Proforma is on the page, display the Jira and Proforma fields at the same time
			renderJiraFields = hasProformaForm ? this.state.asyncFormCreateAppLoaded : true;
		}

		const ProformaView = FormCreateApp ?? AsyncProformaRequestCreateInternal; // This is using root app component from the Locally Consumable version of the package for Proforma Request Create

		return (
			<RecaptchaForm
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
				className={className}
				recaptchaEnabled={this.props.captchaEnabled}
				onSubmit={onSubmit}
				sitekey={sitekey}
				onTokenChange={this.onRecaptchaTokenChange}
				formRef={formRef}
				noValidate
			>
				{({ isExecuting }) => {
					const isProcessing = submitting || isExecuting;
					const isButtonDisable =
						isProcessing || submitDisabled || isAttachmentUploading || isProformaFormPending;

					// Reporter of issue is the one selected in the reporter user picker.
					// If reporter user picker is not displayed, then the reporter is the current user.
					// Suppressing existing violation. Please fix this.
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					const reporterAccountId = fieldValues.reporter ?? accountId;

					const renderProformaFormView = hasProformaForm && (
						<ProformaView
							blockFormSubmission={this.props.blockFormSubmission}
							data-testid={'AsyncProformaRequestCreate'}
							fieldErrors={errors?.fieldErrors}
							knowledgeBase={knowledgeBase}
							locale={locale}
							portalId={portalId}
							registerProformaForm={registerProformaForm}
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							renderFormFinished={this.renderFormFinished.bind(this)}
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							reporterAccountId={reporterAccountId}
							requestTypeId={requestTypeId}
						/>
					);

					return (
						<>
							{this.renderErrorMessage()}

							<SmoothCollapse
								allowOverflowWhenOpen={isRichTextAreaEnabled()}
								expanded={!!isFormExpanded}
							>
								{renderJiraFields && this.renderFields(fields, isProcessing)}
								{!isProformaTTVCUpdateMeasureEnabled() && renderProformaFormView}
								{isProformaTTVCUpdateMeasureEnabled() && (
									<UFOLoadHold name="proforma-jira-portal-create" hold={this.state.shouldHoldUFO}>
										{renderProformaFormView}
									</UFOLoadHold>
								)}

								<ConnectFields
									requestTypeId={requestTypeId}
									portalId={portalId}
									location={REQUEST_CREATE_PANEL}
								/>

								<RequestCreatePropertyPanelModule
									portalId={this.props.portalId}
									projectId={toNumber(this.props.projectId)}
									requestTypeId={this.props.requestTypeId}
									onUpdateRequestPropertyData={updateForgeRequestPropertyData}
								/>

								{renderJiraFields && (
									<ButtonGroup>
										<ThemedButton
											appearance="primary"
											type="submit"
											isDisabled={isButtonDisable}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
											className="request-create-form-submit"
											actionSubjectId="submitRequest"
											interactionName="request-create-submit"
											onMouseEnter={this.props.onHoverSubmitButton}
											testId="request-create-form-submit-button"
										>
											<FormattedMessage {...i18n.sendForm} />
										</ThemedButton>
										<FireScreenEventOnMount />

										<ThemedButton
											appearance="subtle"
											isDisabled={isButtonDisable}
											onClick={onCancel}
											actionSubjectId="cancelRequest"
											testId="request-create-form-cancel-button"
										>
											<FormattedMessage {...i18n.cancelForm} />
										</ThemedButton>

										{(isProcessing || isAttachmentUploading) && <Spinner size="small" />}
									</ButtonGroup>
								)}
							</SmoothCollapse>
						</>
					);
				}}
			</RecaptchaForm>
		);
	}
}
export default withAnalyticsContext({
	componentName: 'requestCreateForm',
})(withAnalyticsEvents()(RequestFormDumb));

const calculateDefaultSummaryValue = (
	urlParamOverride?: string,
	fieldValue?: string,
	defaultValueFromState?: string,
): string | undefined => {
	return urlParamOverride ?? fieldValue ?? defaultValueFromState;
};

const getRichTextAreaFieldValue = (
	urlParamOverride?: string,
	fieldValue?: string,
	defaultValueFromState?: string,
): string | undefined => {
	return urlParamOverride || fieldValue || defaultValueFromState || undefined;
};

const calculateDefaultRichTextValue = memoizeOne(getRichTextAreaFieldValue);
